/**
 * Image Utilities
 *
 * Safe utilities for image creation and manipulation that work across
 * different environments and handle edge cases properly.
 */

/**
 * Safely create an Image element
 * Handles cases where the Image constructor might not be available
 * or has been overridden by libraries/polyfills
 *
 * @returns {HTMLImageElement} A new image element
 */
export const createImageElement = () => {
	try {
		// First try the standard DOM method (most reliable)
		return document.createElement("img");
	} catch {
		// Fallback to Image constructor if createElement fails
		try {
			return new Image();
		} catch {
			// Last resort: create via innerHTML (should never happen in modern browsers)
			const div = document.createElement("div");
			div.innerHTML = "<img>";
			return div.firstChild;
		}
	}
};

/**
 * Preload an image and return a promise that resolves when loaded
 *
 * @param {string} src - Image source URL
 * @param {Object} options - Loading options
 * @param {number} options.timeout - Timeout in milliseconds (default: 10000)
 * @param {boolean} options.crossOrigin - Set crossOrigin attribute
 * @returns {Promise<HTMLImageElement>} Promise that resolves with the loaded image
 */
export const preloadImage = (src, options = {}) => {
	const { timeout = 10000, crossOrigin = false } = options;

	return new Promise((resolve, reject) => {
		if (!src) {
			reject(new Error("Image source is required"));
			return;
		}

		const img = createImageElement();
		let timeoutId;

		// Set up timeout
		if (timeout > 0) {
			timeoutId = setTimeout(() => {
				cleanup();
				reject(new Error(`Image loading timeout after ${timeout}ms: ${src}`));
			}, timeout);
		}

		// Cleanup function
		const cleanup = () => {
			if (timeoutId) {
				clearTimeout(timeoutId);
			}
			img.onload = null;
			img.onerror = null;
			img.onabort = null;
		};

		// Success handler
		img.onload = () => {
			cleanup();
			resolve(img);
		};

		// Error handler
		img.onerror = () => {
			cleanup();
			reject(new Error(`Failed to load image: ${src}`));
		};

		// Abort handler
		img.onabort = () => {
			cleanup();
			reject(new Error(`Image loading aborted: ${src}`));
		};

		// Set crossOrigin if needed
		if (crossOrigin) {
			img.crossOrigin =
				typeof crossOrigin === "string" ? crossOrigin : "anonymous";
		}

		// Start loading
		img.src = src;
	});
};

/**
 * Preload multiple images with controlled concurrency
 *
 * @param {string[]} sources - Array of image source URLs
 * @param {Object} options - Loading options
 * @param {number} options.concurrency - Maximum concurrent loads (default: 3)
 * @param {number} options.timeout - Timeout per image in milliseconds
 * @param {boolean} options.failFast - Stop on first error (default: false)
 * @returns {Promise<Array>} Promise that resolves with array of results
 */
export const preloadImages = async (sources, options = {}) => {
	const { concurrency = 3, failFast = false, ...imageOptions } = options;

	if (!Array.isArray(sources) || sources.length === 0) {
		return [];
	}

	const results = [];
	const chunks = [];

	// Split sources into chunks for controlled concurrency
	for (let i = 0; i < sources.length; i += concurrency) {
		chunks.push(sources.slice(i, i + concurrency));
	}

	// Process chunks sequentially, images within chunks concurrently
	for (const chunk of chunks) {
		const promises = chunk.map(async (src) => {
			try {
				const img = await preloadImage(src, imageOptions);
				return {
					src,
					success: true,
					image: img,
					error: null,
					index: sources.indexOf(src),
				};
			} catch (error) {
				const result = {
					src,
					success: false,
					image: null,
					error: error.message,
					index: sources.indexOf(src),
				};

				if (failFast) {
					throw error;
				}

				return result;
			}
		});

		try {
			const chunkResults = await Promise.allSettled(promises);
			const processedResults = chunkResults.map((result) =>
				result.status === "fulfilled" ? result.value : result.reason,
			);

			results.push(...processedResults);
		} catch (error) {
			if (failFast) {
				throw error;
			}
		}
	}

	return results;
};

/**
 * Check if an image URL is valid and accessible
 *
 * @param {string} src - Image source URL
 * @param {Object} options - Check options
 * @returns {Promise<boolean>} Promise that resolves to true if image is valid
 */
export const isImageValid = async (src, options = {}) => {
	try {
		await preloadImage(src, { ...options, timeout: options.timeout || 5000 });
		return true;
	} catch {
		return false;
	}
};

/**
 * Get image dimensions without fully loading the image
 * Uses a technique to load just enough data to get dimensions
 *
 * @param {string} src - Image source URL
 * @returns {Promise<{width: number, height: number}>} Image dimensions
 */
export const getImageDimensions = (src) => {
	return new Promise((resolve, reject) => {
		const img = createImageElement();

		img.onload = () => {
			resolve({
				width: img.naturalWidth || img.width,
				height: img.naturalHeight || img.height,
			});
		};

		img.onerror = () => {
			reject(new Error(`Failed to get dimensions for image: ${src}`));
		};

		img.src = src;
	});
};

/**
 * Create a data URL from an image element
 *
 * @param {HTMLImageElement} img - Image element
 * @param {string} format - Output format (default: 'image/png')
 * @param {number} quality - Quality for JPEG (0-1, default: 0.92)
 * @returns {string} Data URL
 */
export const imageToDataURL = (img, format = "image/png", quality = 0.92) => {
	const canvas = document.createElement("canvas");
	const ctx = canvas.getContext("2d");

	canvas.width = img.naturalWidth || img.width;
	canvas.height = img.naturalHeight || img.height;

	ctx.drawImage(img, 0, 0);

	return canvas.toDataURL(format, quality);
};

/**
 * Lazy load images using Intersection Observer
 *
 * @param {HTMLElement[]} imageElements - Array of image elements
 * @param {Object} options - Observer options
 * @returns {IntersectionObserver} The observer instance
 */
export const createImageLazyLoader = (imageElements, options = {}) => {
	const {
		threshold = 0.1,
		rootMargin = "50px",
		loadAttribute = "data-src",
		loadedClass = "loaded",
	} = options;

	const observer = new IntersectionObserver(
		(entries) => {
			entries.forEach((entry) => {
				if (entry.isIntersecting) {
					const img = entry.target;
					const src = img.getAttribute(loadAttribute);

					if (src) {
						preloadImage(src)
							.then(() => {
								img.src = src;
								img.classList.add(loadedClass);
								img.removeAttribute(loadAttribute);
								observer.unobserve(img);
							})
							.catch((error) => {
								console.error("Failed to lazy load image:", error);
								img.classList.add("error");
								observer.unobserve(img);
							});
					}
				}
			});
		},
		{
			threshold,
			rootMargin,
		},
	);

	// Observe all provided elements
	imageElements.forEach((img) => {
		if (img && img.nodeType === Node.ELEMENT_NODE) {
			observer.observe(img);
		}
	});

	return observer;
};

/**
 * Generate responsive image srcSet string
 *
 * @param {string} baseUrl - Base image URL
 * @param {number[]} sizes - Array of sizes
 * @param {string} format - Image format (optional)
 * @returns {string} srcSet string
 */
export const generateSrcSet = (
	baseUrl,
	sizes = [320, 640, 1024, 1920],
	format = "",
) => {
	return sizes
		.map((size) => {
			const url = format
				? baseUrl.replace(/\.[^.]+$/, `.${size}w.${format}`)
				: `${baseUrl}?w=${size}`;
			return `${url} ${size}w`;
		})
		.join(", ");
};

/**
 * Image loading statistics tracker
 */
export class ImageLoadingStats {
	constructor() {
		this.stats = {
			total: 0,
			loaded: 0,
			failed: 0,
			cached: 0,
			totalLoadTime: 0,
			averageLoadTime: 0,
		};
		this.loadTimes = new Map();
	}

	startLoad(src) {
		this.stats.total++;
		this.loadTimes.set(src, performance.now());
	}

	recordSuccess(src, fromCache = false) {
		const startTime = this.loadTimes.get(src);
		if (startTime) {
			const loadTime = performance.now() - startTime;
			this.stats.totalLoadTime += loadTime;
			this.stats.averageLoadTime = this.stats.totalLoadTime / this.stats.loaded;
			this.loadTimes.delete(src);
		}

		this.stats.loaded++;
		if (fromCache) {
			this.stats.cached++;
		}
	}

	recordFailure(src) {
		this.stats.failed++;
		this.loadTimes.delete(src);
	}

	getStats() {
		return { ...this.stats };
	}

	reset() {
		this.stats = {
			total: 0,
			loaded: 0,
			failed: 0,
			cached: 0,
			totalLoadTime: 0,
			averageLoadTime: 0,
		};
		this.loadTimes.clear();
	}
}

// Create a global stats instance
export const globalImageStats = new ImageLoadingStats();
